/**
 * The `/development` plan-screen gate (issue #763). Same shape as
 * `lib/recordGate.js` — pure JS, no React, no flag read baked in — for
 * the same testability reason that file's own header gives: this repo
 * mounts no components in unit tests, so a rule that lives only inside JSX
 * is a rule only e2e can check. `pages/DevelopmentPlan.jsx` calls this with
 * its OWN `useCreateDialog()` state (see
 * `createOpen`'s own paragraph below — it is emphatically not shared with
 * the New goal sheet's).
 *
 * D98 (Andreas, 2026-09-24) — the new screen has no "rest of the plan"
 * fold, and this gate builds in no loading frame either: `goalsAnswered`
 * is the one genuine race it has to guard. `goals` starts `[]` before the
 * first `GET /goals` response lands, which is indistinguishable both from
 * a genuinely empty account AND from a caller this route was never built
 * for. While no real answer has landed, `showNewPlan` stays false and the
 * caller falls through to today's page (unaffected, same as before this
 * issue) rather than a dedicated loading frame: `Plan.dc.html` draws no
 * loading state for this slice, unlike `EmptyRecord.dc.html`'s own (#760,
 * B3/#465).
 *
 * `isPlayer` — fix round, B1. D92 (decisions-2026-09-24.md) is explicit:
 * `DestinationRow`'s "every caller of this component is player-only", and
 * this screen renders one unconditionally. Found live, on the fix round's
 * own review: a coach at `/development?new=1` got the create screen AND
 * the player destination row, because `createOpen` (below) used to win
 * BEFORE any player check existed at all — there wasn't one. `isPlayer` now
 * gates the ENTIRE gate, `createOpen` included, so a non-player never
 * reaches the create screen by any path — they see today's page, exactly
 * as the flag-off leg does (that page has no role guard either, and never
 * needed one: it just renders its own empty/failed states for a caller
 * `GET /goals` 400s).
 *
 * `goalsAnswered` — found live, on the first flag-on responsive-harness
 * run, BEFORE `isPlayer` existed: `/development` "has no role guard and IS
 * reachable as a coach", and `GET /goals` with no `player_id` is a
 * DETERMINISTIC 400 for a coach caller — an ANSWER, not an unknown, so the
 * page's separate `goalsFailed` state correctly stays `false` for it (the
 * #271 rule). But `goalsFailed: false` plus a `[]` count reads exactly like
 * "a real 200 confirmed zero goals" to a gate that only checked those two.
 * `goalsAnswered` is the caller's own "a genuine 200 landed" flag, set ONLY
 * in `load`'s success branch, never in `.catch` (a coach's deterministic
 * 400 included) — so this gate asks for a CONFIRMED empty list, not merely
 * an unanswered or refused one. `isPlayer` is the belt to this brace's
 * suspenders: even if `goalsAnswered` were ever wrong for some future
 * non-player caller, `isPlayer` alone still refuses the screen.
 *
 * `goals` counting — RR1 (Andreas's ruling, second fix round). A brief
 * literal reading ("no OPEN goal") shipped in the first fix round: the
 * gate counted only non-`completed` goals (`normalizeGoalStatus`, the same
 * filter the goals list uses), so a player whose only goals were `completed`
 * got the create screen again. Andreas's ruling overrides that reading for
 * this case: it hid the player's own history — a player with only
 * completed goals could no longer REACH them, because the whole tabs page
 * (and the old dialog, and the goals list's "Completed" section) is exactly
 * what the create screen replaces. The gate now counts EVERY goal, `goals.
 * length === 0` — "no goal at all", not "no open goal" — so a player with
 * only completed goals gets today's page, their history and "New goal"
 * intact, same as any other player who already has something to show.
 *
 * `onGoalTab` — also found live: journey 13 (`/development?tab=identity`)
 * broke on the first flag-on full-suite run, because an earlier version of
 * the "no goal" auto-swap had no tab awareness at all — a zero-goal player
 * asking for Identity & Vision, Reflections or Coach Feedback got the
 * create-a-plan screen instead, silently losing three tabs' worth of
 * reachability for every player who hasn't made a goal yet. The auto-swap
 * (never `createOpen`) is now scoped to the tab a bare `/development`
 * visit already resolves to: no `?tab=` at all, or `short`/`long` — the
 * two tabs the New goal sheet's own goal data belongs to.
 * `identity`/`reflections`/`feedback`/`ai` are UNCHANGED: `dev-new-goal`
 * and the old tab strip stay reachable there regardless of goal count,
 * exactly as before this issue.
 *
 * `createOpen` — `pages/DevelopmentPlan.jsx`'s OWN `useCreateDialog()`
 * instance, DELIBERATELY NOT shared with the New goal sheet's (a fix-round
 * finding of its own, PR #859's original review round: sharing one
 * instance meant every `dev-new-goal` CLICK, not just the URL, fed this
 * gate, so clicking "New goal" from a non-goal tab where a plan already
 * existed swapped the WHOLE PAGE mid-click and the old dialog never got a
 * chance to open — broke every `/development` cell in the responsive
 * harness). Gated on `isPlayer` (B1) but NOT on `goalsAnswered`/
 * `onGoalTab`/goal count: the brief is explicit that the screen shows
 * "when the player has no [goal], OR when the page is opened with
 * `?new=1`", and creating a new goal needs no read of the existing list at
 * all — `dev-new-goal` was reachable from EVERY tab before this issue (it
 * renders above `<Tabs>`), and `?new=1` keeps that "quick create from
 * anywhere" reach for a player, whatever their existing goals look like.
 *
 * @param {object} args
 * @param {boolean} args.isPlayer — `user?.role === "player"`, read by the
 *   caller from `useAuth()`. `false`/`undefined` (role not yet resolved,
 *   or a coach/admin) refuses the screen outright, fail-closed, the same
 *   polarity `lib/activityCreate.js`'s own `activityCreateOptions` uses.
 * @param {boolean} args.createOpen — `DevelopmentPlan.jsx`'s own
 *   `useCreateDialog()` state (never the New goal sheet's).
 * @param {boolean} args.onGoalTab — the requested tab is absent (a bare
 *   `/development` visit) or `short`/`long` — never true for
 *   `identity`/`reflections`/`feedback`/`ai`.
 * @param {boolean} args.goalsAnswered — a genuine `GET /goals` 200 has
 *   landed (set only in the success branch — see this file's own header).
 * @param {Array} args.goals — the raw goals array; EVERY goal counts
 *   (RR1) — a player with only `completed` goals still has "a goal", and
 *   keeps today's page.
 * @returns {{ showNewPlan: boolean }}
 */
export function computePlanGate({ isPlayer, createOpen, onGoalTab, goalsAnswered, goals }) {
  const goalsCount = (goals || []).length;
  const showNewPlan =
    !!isPlayer && (createOpen || (onGoalTab && goalsAnswered && goalsCount === 0));
  return { showNewPlan };
}
